/* eslint-disable max-lines */
/* eslint-disable no-magic-numbers */

/* eslint-disable id-denylist */
'use client';

import React, { useMemo } from 'react';

import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type ColumnFiltersState,
    type SortingState,
    type VisibilityState,
    type RowSelectionState,
    filterFns
} from '@tanstack/react-table';

import {
    Table,
    TableBody,
    TableCell,
    TableRow
} from '@/global/base/table.tsx';
import { Label } from '@radix-ui/react-label';
import { DialogTitle } from '@radix-ui/react-dialog';
import { formatISOToDisplayDate } from '../../lib/utils';
import { isValid, parseISO } from 'date-fns';
import { AccessType } from '../../lib/types/dto/authentication';
import Pagination from './Pagination';
import CustomTableHeader from './CustomTableHeader';
import Filters from './Filters';
import { Skeleton } from '@/global/base/skeleton.tsx';
import { Checkbox } from '@/global/base/checkbox.tsx';
import { Button } from '@/global/base/button.tsx';
import {
    Dialog, DialogContent, DialogFooter, DialogPortal
} from '@/global/base/dialog.tsx';
import RestrictedComponent from '@/global/panels/RestrictedComponent.tsx';
import type { Option } from '@/global/form/FilteredSelectField.tsx';
import {
    dateFilter, dateOrStringFilter, dateRangeFilter
} from './filterFunctions';
import { buildFilters, buildOrdering } from './DataTableUtilities';

type ServerPagination = {
    enabled: true;
    pageIndex: number;
    pageSize: number;
    totalRows: number;
    onPageChange: (pageIndex: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    // called with server-friendly filters: { columnId: string -> string }
    onFilterChange?: (filters: Record<string, string | undefined>) => void;
    // called with ordering string expected by backend (e.g. "name" or "-name")
    onSortChange?: (ordering: string | undefined) => void;
};

type DataTableProperties<TData, TValue> = {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    tableTitle: string;
    onRowClick?: (row: TData) => void;
    BulkActionComponent?: React.ComponentType<{ rows: TData[]; clearRows: () => void }>;
    initialState?: {
        sorting?: SortingState;
    };
    loading?: boolean;
    getRowTestId?: (row: TData) => string;
    serverPagination?: ServerPagination;
    /** When set, drives the global "Search..." input (e.g. `?search=` on Manage Patients). Omit to leave the field fully internal. */
    syncGlobalFilter?: string;
};

// TODO: This component can definitely use some reorganisation
// The JSX contains a lot of logic making the code harder to read

export type ColumnMeta = {
    align?: 'center' | 'right' | 'left';
    type?: 'text' | 'number' | 'date' | 'dateRange' | 'select' | 'dateOrString';
    options?: Option[] | string[];
    customHeaderComponent?: boolean;
    mustShow?: boolean;
    disableFilterMenu?: boolean;

};

const slugifyTableTitle = (title: string | undefined) => {
    if (!title || !String(title).trim()) return 'data-table';
    return String(title)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'data-table';
};

const DataTable = <TData, TValue>({
    columns,
    data,
    tableTitle,
    onRowClick,
    initialState = {},
    BulkActionComponent,
    loading = false,
    getRowTestId,
    serverPagination,
    syncGlobalFilter
}: DataTableProperties<TData, TValue>) => {
    const tableSlug = slugifyTableTitle(tableTitle);
    const [sorting, setSorting] = React.useState<SortingState>(initialState.sorting || []);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
    const [globalFilter, setGlobalFilter] = React.useState('');
    const [showColumnModal, setShowColumnModal] = React.useState(false);
    const [columnVisibilityLoaded, setColumnVisibilityLoaded] = React.useState(false);

    const filterDebounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

    // Deep-link / navbar `?search=` → same value as the table toolbar search (does not run when prop is omitted).
    React.useEffect(() => {
        if (syncGlobalFilter === undefined) return;
        setGlobalFilter(syncGlobalFilter);
    }, [syncGlobalFilter]);

    const LOCAL_STORAGE_KEY = `column-visibility-${tableTitle}`;

    // Load ColumnVisibility from localStorage on mount
    React.useEffect(() => {
        const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (saved) {
            try {
                setColumnVisibility(JSON.parse(saved) as VisibilityState);
            } catch {
                // Ignore JSON parse errors and use default visibility
            }
        }
        setColumnVisibilityLoaded(true);
    }, [LOCAL_STORAGE_KEY]);

    // If server pagination is enabled we expect data to be a single page's rows.
    const tableData = useMemo(
        () => { return loading ? Array(10).fill({}) as TData[] : data; },
        [loading, data]
    );

    const processedColumns = useMemo(() => {
        return columns.map((column) => {
            const meta = column.meta as ColumnMeta;
            if (meta?.type === 'number') {
                return {
                    ...column,
                    filterFn: filterFns.equalsString
                };
            }
            if (meta?.type === 'dateRange') {
                return {
                    ...column,
                    filterFn: dateRangeFilter,
                    cell: (row: { getValue: (columnId: string) => string }) => formatISOToDisplayDate(row.getValue(column.id as string))
                };
            }
            if (meta?.type === 'date') {
                return {
                    ...column,
                    cell: (row: { getValue: (columnId: string) => string }) => formatISOToDisplayDate(row.getValue(column.id as string)),
                    filterFn: dateFilter
                };
            }
            if (meta?.type === 'dateOrString') {
                return {
                    ...column,
                    cell: (row: { getValue: (columnId: string) => string }) => {
                        const value = row.getValue(column.id as string);

                        if (typeof value !== 'string' || !isValid(parseISO(value))) {
                            return value ?? '';
                        }

                        return formatISOToDisplayDate(value);
                    },
                    filterFn: dateOrStringFilter
                };
            }

            return column;
        });
    }, [columns]);

    // treat "no serverPagination prop" the same as disabled server pagination
    const manualPagination = Boolean(serverPagination?.enabled);
    const externalPageIndex = manualPagination ? serverPagination?.pageIndex : undefined;
    const externalPageSize = manualPagination ? serverPagination?.pageSize : undefined;
    const externalTotal = manualPagination ? serverPagination?.totalRows : undefined;
    const pageCount = manualPagination && externalTotal != null ? Math.max(1, Math.ceil(externalTotal / (externalPageSize || 1))) : undefined;

    const table = useReactTable({
        data: tableData,
        columns: processedColumns,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
            globalFilter,
            ...manualPagination && {
                pagination: {
                    pageIndex: externalPageIndex ?? 0,
                    pageSize: externalPageSize ?? 25
                }
            }
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        enableRowSelection: true,
        enableMultiRowSelection: true,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        // only enable client-side filtered row model when NOT using server pagination
        getFilteredRowModel: manualPagination ? undefined : getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        // client-only filtering/sorting helpers kept, but manualPagination disables client filtering model
        manualPagination,
        pageCount
    });

    // react-table's internal pagination controls will call these; forward to parent for server pagination
    const handlePageChange = (newIndex: number) => {
        if (manualPagination && serverPagination) {
            serverPagination.onPageChange(newIndex);
        } else {
            table.setPageIndex(newIndex);
        }
    };

    const handlePageSizeChange = (newSize: number) => {
        if (manualPagination && serverPagination) {
            serverPagination.onPageSizeChange(newSize);
        } else {
            table.setPageSize(newSize);
        }
    };

    // debounce before firing server filter/sort to avoid noisy calls while typing
    React.useEffect(() => {
        if (!manualPagination) return;

        // Clear any pending timeout
        if (filterDebounceTimerRef.current) {
            clearTimeout(filterDebounceTimerRef.current);
        }

        // Schedule new timeout
        filterDebounceTimerRef.current = setTimeout(() => {
            const filters = buildFilters(table, columnFilters, globalFilter);
            serverPagination?.onFilterChange?.(filters);

            const ordering = buildOrdering(table, sorting);
            if (ordering) serverPagination?.onSortChange?.(ordering);

            filterDebounceTimerRef.current = null;
        }, 500); // debounce

        return () => {
            if (filterDebounceTimerRef.current) {
                clearTimeout(filterDebounceTimerRef.current);
                filterDebounceTimerRef.current = null;
            }
        };
        // Intentionally only react to filter/sort state; adding serverPagination or table
        // would re-run on every parent re-render and cause duplicate fetches.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        manualPagination,
        serverPagination?.enabled,
        columnFilters,
        globalFilter,
        sorting
    ]);

    const pageIndex = table.getState().pagination?.pageIndex ?? 0;
    const pageSize = table.getState().pagination?.pageSize ?? 10;
    // For server pagination totalRows comes from externalTotal, otherwise compute from filtered rows
    const totalRows = manualPagination && externalTotal != null ? externalTotal : table.getFilteredRowModel().rows.length;

    const startRow = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
    const endRow = Math.min((pageIndex + 1) * pageSize, totalRows);

    const selectedRowCount = Object.keys(rowSelection).length;
    const selectedRows = Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map((key) => table.getRowModel().rows.find((row) => row.id === key)?.original)
        .filter((row): row is TData => row !== undefined);

    // if columnVisibilityLoaded is false, we return a placeholder to avoid rendering flaws
    if (!columnVisibilityLoaded) {
        return (
            <div
                className="h-[600px]"
                data-testid={`${tableSlug}-loading-placeholder`}
            />
        );
    }

    return (
        <div data-testid={`${tableSlug}-data-table`}>
            {/* Filters */}
            <Filters
                table={table}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                setColumnFilters={setColumnFilters}
                setShowColumnModal={setShowColumnModal}
                tableSlug={tableSlug}
            />

            {/* Bulk Actions */}
            <RestrictedComponent allow={AccessType.NURSE}>
                {selectedRowCount > 0 &&
                    <div
                        className="flex justify-end items-center pt-2 gap-4"
                        data-testid={`${tableSlug}-bulk-actions`}
                    >
                        <p
                            className="text-sm font-medium"
                            data-testid={`${tableSlug}-bulk-selection-count`}
                        >
                            {selectedRowCount} item{selectedRowCount > 1 ? 's' : ''} selected
                        </p>
                        <div className="flex gap-4">
                            <div className="flex gap-4">
                                {BulkActionComponent && <BulkActionComponent rows={selectedRows} clearRows={() => setRowSelection({})} />}
                            </div>
                        </div>
                    </div>
                }
            </RestrictedComponent>

            {/* Edit Columns Button */}
            <Dialog open={showColumnModal} onOpenChange={setShowColumnModal}>
                <DialogTitle className="hidden">Edit Column Preferences</DialogTitle>
                <DialogPortal>
                    <DialogContent
                        className="text-center"
                        data-testid={`${tableSlug}-edit-columns-modal`}
                    >
                        <div className="w-full flex justify-center mb-2">
                            <img className="w-[60px]" src="/images/folder.png" alt="img" />
                        </div>
                        <p
                            className="text-secondary-foreground mb-2"
                            data-testid={`${tableSlug}-edit-columns-description`}
                        >
                            Please select the columns you wish to display on the table
                        </p>
                        <div
                            className="space-y-2"
                            data-testid={`${tableSlug}-edit-columns-list`}
                        >
                            {table.getAllLeafColumns()
                                .filter((col) => col.id !== 'select')
                                .map((col) => {
                                    return <div
                                        key={col.id}
                                        className="flex items-center gap-2"
                                        data-testid={`${tableSlug}-edit-column-row-${col.id}`}
                                    >
                                        <div className="flex justify-between w-full px-2 border-b-2 border-gray-200">
                                            <Label
                                                className="font-bold"
                                                key={`col-toggle-${col.id}`}
                                                data-testid={`${tableSlug}-edit-column-label-${col.id}`}
                                            >
                                                {col.columnDef.header as string}
                                            </Label>
                                            <Checkbox
                                                className="h-5 w-5"
                                                checked={col.getIsVisible()}
                                                onCheckedChange={() => setColumnVisibility((previous) => ({
                                                    ...previous,
                                                    [col.id]: !col.getIsVisible()
                                                }))
                                                }
                                                id={`col-toggle-${col.id}`}
                                                disabled={(col.columnDef.meta as ColumnMeta)?.mustShow}
                                                data-testid={`${tableSlug}-edit-column-checkbox-${col.id}`}
                                            />
                                        </div>
                                    </div>;
                                })
                            }
                        </div>
                        <DialogFooter className="flex sm:justify-center mt-4 gap-6 w-full">
                            <Button
                                className="py-0 w-[120px]"
                                data-testid={`${tableSlug}-edit-columns-save-button`}
                                onClick={() => {
                                    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(columnVisibility));
                                    setShowColumnModal(false);
                                }}
                            >
                                Save
                            </Button>
                            <Button
                                variant="outline"
                                className="py-0 w-[120px]"
                                data-testid={`${tableSlug}-edit-columns-close-button`}
                                onClick={() => setShowColumnModal(false)}
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </DialogPortal>
            </Dialog>

            {/* Table */}
            <div data-testid={`${tableSlug}-table-section`}>
                <div className="flex items-baseline space-x-3 pb-4">
                    <h3
                        className="font-bold ml-2"
                        data-testid={`${tableSlug}-title`}
                    >
                        {tableTitle}
                    </h3>
                </div>
                <Table data-testid={`${tableSlug}-table`}>
                    <thead data-testid={`${tableSlug}-thead`}>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <CustomTableHeader
                                key={headerGroup.id}
                                headerGroup={headerGroup}
                                tableSlug={tableSlug}
                            />
                        ))}
                    </thead>
                    <TableBody data-testid={`${tableSlug}-tbody`}>
                        {table.getRowModel().rows?.length
                            ? table.getRowModel().rows.map((row) => <TableRow
                                key={row.id}
                                data-state={row.getIsSelected() && 'selected'}
                                onClick={() => onRowClick?.(row.original)}
                                className="cursor-pointer hover:bg-gray-100"
                                data-testid={getRowTestId ? getRowTestId(row.original) : `${tableSlug}-row-${row.id}`}
                            >
                                {row.getVisibleCells().map((cell) => {
                                    const alignClass = (() => {
                                        const meta = cell.column.columnDef.meta as {
                                            align?: 'left' | 'center' | 'right';
                                        };

                                        if (meta?.align === 'center') return 'text-center';
                                        if (meta?.align === 'right') return 'text-right';

                                        return 'text-left';
                                    })();

                                    return (
                                        <TableCell
                                            key={cell.id}
                                            className={alignClass}
                                            data-testid={`${tableSlug}-cell-${row.id}-${cell.column.id}`}
                                        >
                                            {loading ? <Skeleton className="w-full h-[24px] rounded-full" /> : flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>)
                            : <TableRow data-testid={`${tableSlug}-empty-row`}>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    <div
                                        className="text-muted-foreground"
                                        data-testid={`${tableSlug}-empty-message`}
                                    >
                                        No results found. Try adjusting your filters or search query.
                                    </div>
                                </TableCell>
                            </TableRow>}
                    </TableBody>
                </Table>

                {/* Pagination */}
                <Pagination
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalRows={totalRows}
                    startRow={startRow}
                    endRow={endRow}
                    pageSizes={['10', '20', '30', '50', '100']}
                    canPreviousPage={manualPagination ? pageIndex > 0 : table.getCanPreviousPage()}
                    canNextPage={manualPagination ? (externalTotal != null ? (pageIndex + 1) * pageSize < externalTotal : table.getCanNextPage()) : table.getCanNextPage()}
                    onPreviousPage={() => handlePageChange(Math.max(0, pageIndex - 1))}
                    onNextPage={() => handlePageChange(pageIndex + 1)}
                    onPageSizeChange={(size) => handlePageSizeChange(size)}
                    tableSlug={tableSlug}
                />
            </div>
        </div>
    );
};

export default DataTable;
